import { Pause, Play, X, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";

export const WALK_STEPS = [
    {
        tab: "package",
        targets: ["change-package-card"],
        text: "A supplier publishes an authorised product change as a bounded package.",
    },
    {
        tab: "impact",
        targets: ["impact-node-partner", "impact-node-provider", "impact-node-clinical", "impact-node-assurance"],
        text: "Each organisation assesses impact locally, inside its own custody boundary.",
    },
    {
        tab: "impact",
        scenario: "denied",
        targets: ["impact-node-clinical", "scenario-note-denied"],
        text: "A provider's red result changes the release plan — the common date is withdrawn.",
    },
    {
        tab: "install",
        scenario: "blocked",
        stage: "authorised",
        targets: ["install-engineer-card"],
        text: "An authorised engineer performs a bounded installation under gated checks.",
    },
    {
        tab: "install",
        scenario: "blocked",
        stage: "blocked",
        targets: ["install-blocked-banner"],
        text: "A failed interface test blocks release — no role can edit the status away.",
    },
    {
        tab: "install",
        scenario: "blocked",
        stage: "accepted",
        targets: ["install-exception-chain"],
        text: "The exception is corrected and rechecked; a fresh passing check clears the block.",
    },
    {
        tab: "install",
        scenario: "blocked",
        stage: "released",
        targets: ["install-final-banner"],
        text: "The clinical service owner — a distinct authority — releases the equipment into service.",
    },
    {
        tab: "service",
        targets: ["feedback-panel"],
        text: "Post-release evidence returns as bounded receipts and informs the next rollout.",
    },
];

export const clearWalkHighlights = () =>
    document.querySelectorAll(".walk-highlight").forEach((el) => el.classList.remove("walk-highlight"));

export const applyWalkHighlights = (targets) => {
    clearWalkHighlights();
    targets.forEach((t, i) => {
        const el = document.querySelector(`[data-testid='${t}']`);
        if (el) {
            el.classList.add("walk-highlight");
            if (i === 0) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    });
};

export const WalkthroughOverlay = ({
    step,
    paused,
    completed,
    onPrev,
    onNext,
    onPauseToggle,
    onExit,
    onReplay,
}) => (
    <div
        className="fixed bottom-5 right-5 z-40 w-[340px] max-w-[calc(100vw-2.5rem)] rounded-sm border border-slate-200 bg-white p-4 shadow-lg"
        data-testid="walkthrough-overlay"
    >
        <style>{`.walk-highlight{outline:2px solid #172554;outline-offset:4px;border-radius:2px;}`}</style>
        <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
                {completed ? "Guided release complete" : `Guided release · Step ${step + 1} of ${WALK_STEPS.length}`}
            </p>
            <button
                onClick={onExit}
                data-testid="walkthrough-exit"
                aria-label="Exit walkthrough"
                className="rounded-sm p-1 text-slate-400 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-700"
            >
                <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
        </div>

        <p className="mt-2 text-sm leading-relaxed text-slate-800" data-testid="walkthrough-step-text">
            {completed
                ? "You can now explore every act, scenario, role view and receipt manually."
                : WALK_STEPS[step].text}
        </p>

        {!completed && (
            <div className="mt-3 flex gap-1">
                {WALK_STEPS.map((_, i) => (
                    <span key={i} className={`h-1 flex-1 rounded-sm ${i <= step ? "bg-blue-950" : "bg-slate-200"}`} />
                ))}
            </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {!completed && (
                <>
                    <CtrlBtn onClick={onPrev} disabled={step === 0} testId="walkthrough-prev">
                        <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Previous
                    </CtrlBtn>
                    <CtrlBtn onClick={onNext} testId="walkthrough-next">
                        Next
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </CtrlBtn>
                    <CtrlBtn onClick={onPauseToggle} testId="walkthrough-pause">
                        {paused ? <Play className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Pause className="h-3.5 w-3.5" strokeWidth={1.5} />}
                        {paused ? "Resume" : "Pause"}
                    </CtrlBtn>
                </>
            )}
            <CtrlBtn onClick={onReplay} testId="walkthrough-replay">
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
                Replay
            </CtrlBtn>
        </div>
    </div>
);

function CtrlBtn({ onClick, disabled, testId, children }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-default disabled:opacity-40"
        >
            {children}
        </button>
    );
}
